import { useEffect, useState } from "react";
import { ShieldCheck, Loader2, PlugZap, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { PageHeader, Spinner, EthicsNote } from "../components/common";
import { api, formatErr } from "../lib/api";

const PROVIDERS = [
  { id: "shopee", name: "Shopee Afiliados", color: "#EE4D2D", docs: "https://affiliate.shopee.com.br/open_api", desc: "Busque ofertas oficiais e gere links curtos de afiliado pela Shopee Affiliate Open API.",
    fields: [{ k: "app_id", l: "App ID" }, { k: "secret", l: "Secret", secret: true }] },
  { id: "meta", name: "Meta (Instagram & Facebook)", color: "#1877F2", docs: "https://developers.facebook.com/docs/graph-api", desc: "Conecte sua conta via Graph API oficial (token de acesso gerado no Meta for Developers).",
    fields: [{ k: "app_id", l: "App ID" }, { k: "app_secret", l: "App Secret", secret: true }, { k: "access_token", l: "Access Token", secret: true }] },
];
const STATUS = { connected: ["Conectado", "text-emerald-300 bg-emerald-500/10"], error: ["Erro", "text-rose-300 bg-rose-500/10"], configured: ["Configurado", "text-amber-300 bg-amber-500/10"], not_configured: ["Não configurado", "text-zinc-400 bg-zinc-800"] };

const ProviderCard = ({ p, data, onChange }) => {
  const [v, setV] = useState({});
  const [busy, setBusy] = useState("");
  useEffect(() => { setV({ app_id: data.app_id || "" }); }, [data.app_id]);
  const [label, cls] = STATUS[data.status] || STATUS.not_configured;

  const save = async (e) => {
    e.preventDefault(); setBusy("save");
    try { const { data: d } = await api.put(`/integrations/${p.id}`, v); onChange(d); toast.success("Credenciais salvas com segurança"); setV({ app_id: d.app_id }); }
    catch (e2) { toast.error(formatErr(e2)); } finally { setBusy(""); }
  };
  const test = async () => {
    setBusy("test");
    try { const { data: d } = await api.post(`/integrations/${p.id}/test`); d.ok ? toast.success(d.message) : toast.error(d.message); onChange({ ...data, status: d.ok ? "connected" : "error", message: d.message }); }
    catch (e) { toast.error(formatErr(e)); } finally { setBusy(""); }
  };

  return (
    <form onSubmit={save} className="card-lux p-5 space-y-4 rise" data-testid={`integration-card-${p.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-3"><span className="h-10 w-10 rounded-xl grid place-items-center" style={{ background: p.color + "22" }}><PlugZap className="h-5 w-5" style={{ color: p.color }} /></span>
          <div><div className="font-display text-lg font-bold">{p.name}</div><a href={p.docs} target="_blank" rel="noreferrer" className="text-[11px] text-zinc-500 hover:text-amber-400 inline-flex items-center gap-1">Documentação oficial <ExternalLink className="h-3 w-3" /></a></div></div>
        <span className={`text-[11px] px-2 py-1 rounded-full shrink-0 ${cls}`} data-testid={`integration-status-${p.id}`}>{label}</span>
      </div>
      <p className="text-xs text-zinc-400">{p.desc}</p>
      {p.fields.map((f) => (
        <div key={f.k} className="space-y-1.5">
          <Label className="text-xs text-zinc-400">{f.l}</Label>
          <Input type={f.secret ? "password" : "text"} className="bg-zinc-900 border-zinc-800" value={v[f.k] || ""} onChange={(e) => setV({ ...v, [f.k]: e.target.value })}
            placeholder={f.secret && data[`has_${f.k}`] ? "•••••••• (salvo — preencha para trocar)" : ""} data-testid={`integration-${p.id}-${f.k}`} />
        </div>
      ))}
      {data.message && <div className="text-xs text-zinc-400" data-testid={`integration-message-${p.id}`}>{data.message}</div>}
      <div className="grid grid-cols-2 gap-2">
        <Button type="submit" className="btn-gold rounded-full" disabled={!!busy} data-testid={`integration-save-${p.id}`}>{busy === "save" && <Loader2 className="h-4 w-4 animate-spin mr-1" />}Salvar</Button>
        <Button type="button" variant="outline" className="rounded-full border-zinc-700" disabled={!!busy || data.status === "not_configured"} onClick={test} data-testid={`integration-test-${p.id}`}>{busy === "test" && <Loader2 className="h-4 w-4 animate-spin mr-1" />}Testar conexão</Button>
      </div>
    </form>
  );
};

export default function Integrations() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/integrations").then((r) => setData(r.data)); }, []);
  if (!data) return <Spinner />;
  return (
    <div>
      <PageHeader eyebrow="Conexões" title="Integrações oficiais" desc="Somente APIs oficiais, com suas próprias credenciais. Segredos são criptografados e nunca exibidos." />
      <div className="grid md:grid-cols-2 gap-4">
        {PROVIDERS.map((p) => <ProviderCard key={p.id} p={p} data={data[p.id]} onChange={(d) => setData((s) => ({ ...s, [p.id]: d }))} />)}
      </div>
      <div className="mt-5"><EthicsNote><ShieldCheck className="h-3.5 w-3.5 inline mr-1" />Modo conformidade: sem scraping, sem automação de curtidas/seguidores, sem envio em massa e sem burlar limites das plataformas. Publicações são sempre revisadas e feitas por você.</EthicsNote></div>
    </div>
  );
}
